import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input } from "@/components/ui/Input";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";

import {
  searchInsideSalesWirelessPortalOrganizations,
  type WirelessPortalAccountItem,
} from "./insideSalesWirelessPortalAccountsApi";

export function ReviewWirelessPortalOrganizationModal(props: {
  account: WirelessPortalAccountItem | null;
  pending: boolean;
  onClose: () => void;
  onSave: (input: { name: string; domain: string }) => void;
}) {
  const { account, pending, onClose, onSave } = props;
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  useEffect(() => {
    if (account == null) return;
    setName(account.portalOrganizationName ?? "");
    setDomain("");
    setSearch("");
    setSubmittedSearch("");
  }, [account]);

  const searchQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-organizations", submittedSearch],
    queryFn: () => searchInsideSalesWirelessPortalOrganizations(submittedSearch),
    enabled: account != null && submittedSearch.trim() !== "",
  });

  if (account == null) return null;

  const canSave = name.trim() !== "" && !pending;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("form")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-portal-org-title"
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <h2 id="review-portal-org-title" className="text-base font-semibold text-slate-900">
            {account.salesOrderNumber}
          </h2>
          <HoverHintWithGlyph hoverHint="Ticket details for this wireless portal account. You can update the name and domain text on the linked portal organization. Search is only to see whether another organization already exists." />
        </div>
        <form
          className={`${hubModalBodyScrollClassName()} space-y-3 px-4 py-4`}
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSave) return;
            onSave({ name: name.trim(), domain: domain.trim() });
          }}
        >
          <div className="space-y-1 text-sm text-slate-700">
            <p>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sales order</span>
              <span className="mt-0.5 block font-medium text-slate-900">
                {Number.isFinite(Number(account.salesOrderId)) && Number(account.salesOrderId) > 0 ? (
                  <SalesOrderDetailLink
                    salesOrderId={Number(account.salesOrderId)}
                    salesOrderNumber={account.salesOrderNumber}
                  />
                ) : (
                  account.salesOrderNumber
                )}
              </span>
            </p>
            <p>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">End user</span>
              <span className="mt-0.5 block">{account.endUserName || `FS ${account.freshsalesAccountId}`}</span>
            </p>
            <p>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Portal organization</span>
              <span className="mt-0.5 block">
                {account.portalOrganizationName || "None yet"}
                {account.portalOrganizationId != null ? ` (org ${account.portalOrganizationId})` : ""}
              </span>
            </p>
            {account.hasPortalAdmin != null ? (
              <p>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Admin</span>
                <span className="mt-0.5 block">
                  {account.hasPortalAdmin
                    ? account.portalAdminSummary || "Admin created"
                    : "No organization admin yet"}
                </span>
              </p>
            ) : null}
            {account.orderShipByDate ? (
              <p>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Order ship by</span>
                <span className="mt-0.5 block" title="Promised ship-by date on the sales order">
                  {account.orderShipByDate.slice(0, 10)}
                </span>
              </p>
            ) : null}
            {(account.manufacturingShipByDates ?? []).length > 0 ? (
              <p>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mfr ship by</span>
                <span
                  className="mt-0.5 block"
                  title="Manufacturing batch ship-by date for wireless lines on this order"
                >
                  {account.manufacturingShipByDates.map((date) => date.slice(0, 10)).join(", ")}
                </span>
              </p>
            ) : null}
            {account.errorMessage ? <p className="text-rose-700">{account.errorMessage}</p> : null}
          </div>
          <label className="block space-y-1 text-sm text-slate-700">
            <span>Name</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="block space-y-1 text-sm text-slate-700">
            <span>Domain (optional)</span>
            <Input
              value={domain}
              placeholder="Leave blank to keep the current domain"
              onChange={(event) => setDomain(event.target.value)}
            />
          </label>
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <div className="text-sm font-medium text-slate-800">Search other organizations</div>
            <div className="flex gap-2">
              <Input
                value={search}
                placeholder="Name, domain, or Freshsales id"
                onChange={(event) => setSearch(event.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => setSubmittedSearch(search.trim())}
                disabled={search.trim() === ""}
              >
                Search
              </Button>
            </div>
            {searchQuery.isFetching ? <p className="text-xs text-slate-500">Searching...</p> : null}
            {searchQuery.isError ? (
              <p className="text-xs text-rose-700">
                {searchQuery.error instanceof ApiError
                  ? searchQuery.error.message
                  : "Could not search portal organizations."}
              </p>
            ) : null}
            {submittedSearch !== "" && searchQuery.data?.organizations.length === 0 && !searchQuery.isFetching ? (
              <p className="text-xs text-slate-500">No matching organizations.</p>
            ) : null}
            <ul className="space-y-2">
              {(searchQuery.data?.organizations ?? []).map((organization) => (
                <li key={organization.organizationId} className="rounded border border-slate-200 px-2 py-1.5 text-xs text-slate-700">
                  <div className="font-medium text-slate-900">{organization.name}</div>
                  <div>
                    Org {organization.organizationId}
                    {organization.freshsalesAccountId ? `, Freshsales ${organization.freshsalesAccountId}` : ""}
                    {organization.domain ? `, ${organization.domain}` : ""}
                  </div>
                  {organization.organizationId === account.portalOrganizationId ? (
                    <div className="text-emerald-800">This is the organization on this task.</div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave} isLoading={pending}>
              Save organization text
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
